/**
 * Tenant Edit Dialog Component
 * 
 * Reusable dialog for creating and editing tenants.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Loader2 } from 'lucide-react';
import { ALL_LANGUAGES, PRIMARY_LANGUAGE } from '@/shared/config/languages';
import { useLanguage } from '@/core/LanguageContext';
import type { Tenant, CreateTenantInput, UpdateTenantInput, TenantSettings } from '../types';

interface TenantEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: Tenant | null;
  onSave: (data: CreateTenantInput | UpdateTenantInput) => void;
  isPending: boolean;
}

export function TenantEditDialog({
  open,
  onOpenChange,
  tenant,
  onSave,
  isPending,
}: TenantEditDialogProps) {
  const { t } = useLanguage();
  const isEditing = !!tenant;

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [microsoftTenantId, setMicrosoftTenantId] = useState('');
  const [secondaryLanguage, setSecondaryLanguage] = useState<string>('none');

  // Reset form when dialog opens/closes or tenant changes
  useEffect(() => {
    if (open) {
      if (tenant) {
        setName(tenant.name);
        setCode(tenant.code);
        setMicrosoftTenantId(tenant.microsoft_tenant_id || '');
        setSecondaryLanguage(tenant.settings?.secondary_language || 'none');
      } else {
        setName('');
        setCode('');
        setMicrosoftTenantId('');
        setSecondaryLanguage('none');
      }
    }
  }, [open, tenant]);

  const handleSave = () => {
    const settings: TenantSettings = {};
    if (secondaryLanguage !== 'none') {
      settings.secondary_language = secondaryLanguage;
    }

    const data: CreateTenantInput | UpdateTenantInput = {
      name,
      code: code.toUpperCase(),
      microsoft_tenant_id: microsoftTenantId || null,
      settings: Object.keys(settings).length > 0 ? settings : null,
    };

    onSave(data);
  };

  const canSave = name.trim() !== '' && code.trim() !== '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('superadmin.tenants.editTitle', 'Edit Tenant')
              : t('superadmin.tenants.createTitle', 'Add New Tenant')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('superadmin.tenants.editDescription', 'Update tenant details.')
              : t('superadmin.tenants.createDescription', 'Create a new organization tenant.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="tenant-name">
              {t('superadmin.tenants.nameLabel', 'Tenant Name')}
            </Label>
            <Input
              id="tenant-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('superadmin.tenants.namePlaceholder', 'e.g., Acme Corporation')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-code">
              {t('superadmin.tenants.codeLabel', 'Tenant Code')}
            </Label>
            <Input
              id="tenant-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t('superadmin.tenants.codePlaceholder', 'e.g., ACME')}
              maxLength={10}
            />
            <p className="text-xs text-muted-foreground">
              {t('superadmin.tenants.codeHint', 'Unique identifier (max 10 characters)')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-microsoft-tenant">
              {t('superadmin.tenants.microsoftLabel', 'Microsoft 365 Tenant ID')}
            </Label>
            <Input
              id="tenant-microsoft-tenant"
              value={microsoftTenantId}
              onChange={(e) => setMicrosoftTenantId(e.target.value)}
              placeholder="e.g., 8d1f9b7e-8c72-4ea8-8d08-0e24e12004f6"
            />
            <p className="text-xs text-muted-foreground">
              {t(
                'superadmin.tenants.microsoftHint',
                'Users from this Microsoft tenant will be auto-assigned to this organization'
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-secondary-language">
              {t('superadmin.tenants.languageLabel', 'Secondary Language')}
            </Label>
            <Select value={secondaryLanguage} onValueChange={setSecondaryLanguage}>
              <SelectTrigger>
                <SelectValue
                  placeholder={t('superadmin.tenants.languagePlaceholder', 'Select language')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  {t('superadmin.tenants.languageNone', 'None (English only)')}
                </SelectItem>
                {ALL_LANGUAGES.filter((l) => l.code !== PRIMARY_LANGUAGE).map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t(
                'superadmin.tenants.languageHint',
                'Enable a second language for checklist translations'
              )}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="industrial" onClick={handleSave} disabled={!canSave || isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditing
              ? t('superadmin.tenants.updateButton', 'Update Tenant')
              : t('superadmin.tenants.createButton', 'Create Tenant')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
